// React hook for cookieless analytics tracking
// This provides easy tracking methods that work regardless of cookie consent

import { useCallback } from 'react';
import { trackEventWithConsent, trackPageViewWithConsent } from '../utils/analyticsWithConsentMode';
import { trackServerSideEvent, trackButtonClickServerSide, trackConversionServerSide } from '../utils/serverSideAnalytics';
import { trackCampaignPerformance, getStoredUTMParameters } from '../utils/utmTracking';

export const useAnalytics = () => {
  // Track events with both client-side consent mode and server-side fallback
  const trackEvent = useCallback((eventName, parameters = {}) => {
    // Client-side tracking (Google Analytics with consent mode)
    trackEventWithConsent(eventName, parameters);
    
    // Server-side tracking (no cookies required)
    trackServerSideEvent({
      event: eventName,
      ...parameters,
      utm_params: getStoredUTMParameters()
    });
  }, []);

  // Track page views
  const trackPageView = useCallback((pageTitle, additionalData = {}) => {
    // Client-side tracking
    trackPageViewWithConsent(pageTitle, window.location.href);
    
    // Server-side tracking
    trackServerSideEvent({
      event: 'page_view',
      page_title: pageTitle,
      page_path: window.location.pathname,
      page_search: window.location.search,
      utm_params: getStoredUTMParameters(),
      ...additionalData
    });
  }, []);

  // Track button clicks (especially for ad campaigns)
  const trackButtonClick = useCallback((buttonName, additionalData = {}) => {
    const campaignData = trackCampaignPerformance('button_click', {
      button_name: buttonName,
      ...additionalData
    });
    
    // Client-side tracking
    trackEvent('click', campaignData);
    
    // Server-side tracking
    trackButtonClickServerSide(buttonName, campaignData.utm_campaign, campaignData);
  }, [trackEvent]);

  // Track conversions
  const trackConversion = useCallback((conversionType, value, currency = 'CAD', additionalData = {}) => {
    const conversionData = {
      conversion_type: conversionType,
      value: value,
      currency: currency,
      utm_params: getStoredUTMParameters(),
      ...additionalData
    };
    
    // Client-side tracking
    trackEvent('conversion', conversionData);
    
    // Server-side tracking
    trackConversionServerSide(conversionType, value, currency, conversionData);
  }, [trackEvent]);

  // Track ad campaign interactions
  const trackAdInteraction = useCallback((interactionType, additionalData = {}) => {
    const campaignData = trackCampaignPerformance('ad_interaction', {
      interaction_type: interactionType,
      ...additionalData
    });
    
    trackEvent('ad_interaction', campaignData);
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackButtonClick,
    trackConversion,
    trackAdInteraction
  };
};
